import React from 'react';
import {
  TG, BJ, BF, CV, CI, GM, GH, GN, GW, LR, ML, NE, NG, SN, SL,
} from 'country-flag-icons/react/3x2';
import { PAYS_CEDEAO } from '../variables/paysCedeao';

/**
 * Anneau des drapeaux CEDEAO tournant autour d'un contenu central (le logo).
 * Le Togo - pays hôte du BNCB - est épinglé en haut de l'anneau, immobile et
 * mis en lumière ; les autres États tournent lentement autour.
 *
 * Drapeaux en SVG (et non en emoji) : Windows ne possède pas les glyphes des
 * drapeaux et afficherait « TG » au lieu de 🇹🇬.
 */

/* Drapeaux SVG indexés par code ISO2, alignés sur la source unique paysCedeao. */
const FLAGS = { TG, BJ, BF, CV, CI, GM, GH, GN, GW, LR, ML, NE, NG, SN, SL };

const css = `
.cb-orbit {
  position: relative;
  width: calc(var(--cb-orbit-r) * 2 + 56px);
  height: calc(var(--cb-orbit-r) * 2 + 56px);
  display: flex; align-items: center; justify-content: center;
  transform: scale(var(--cb-orbit-scale, 1));
}

/* Rail circulaire sur lequel « reposent » les drapeaux */
.cb-orbit-rail {
  position: absolute; inset: 28px;
  border-radius: 50%;
  border: 1px dashed rgba(255,255,255,0.08);
  pointer-events: none;
}
/* Lueur derrière le logo, pour le détacher de l'anneau */
.cb-orbit-glow {
  position: absolute; inset: 22%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(61,160,104,0.18) 0%, transparent 70%);
  pointer-events: none;
}

.cb-orbit-core { position: relative; z-index: 3; }

/* Anneau en rotation : chaque drapeau est contre-tourné pour rester droit */
.cb-orbit-spin {
  position: absolute; inset: 0; z-index: 2;
  animation: cb-orbit-turn var(--cb-orbit-duration) linear infinite;
}
.cb-orbit-slot { position: absolute; top: 50%; left: 50%; }
.cb-orbit-upright {
  animation: cb-orbit-turn-back var(--cb-orbit-duration) linear infinite;
}

.cb-orbit-flag {
  display: block; width: 30px; height: 20px;
  border-radius: 3px;
  border: 1px solid rgba(255,255,255,0.28);
  box-shadow: 0 3px 10px rgba(0,0,0,0.35);
  opacity: 0.82;
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.cb-orbit-slot:hover .cb-orbit-flag {
  transform: scale(1.35);
  opacity: 1;
}

/* Togo : épinglé à 12 h, immobile, plus grand, halo doré */
.cb-orbit-togo {
  position: absolute; top: 50%; left: 50%; z-index: 4;
  transform: translate(-50%, -50%) translateY(calc(var(--cb-orbit-r) * -1));
  display: flex; flex-direction: column; align-items: center; gap: 7px;
}
.cb-orbit-togo .cb-orbit-flag {
  width: 46px; height: 31px;
  border: 2px solid var(--acc, #d5c93b);
  border-radius: 4px;
  opacity: 1;
  animation: cb-orbit-pulse 3.2s ease-in-out infinite;
}
.cb-orbit-togo-label {
  font-size: 10px; font-weight: 600; letter-spacing: 1.4px;
  text-transform: uppercase; color: var(--acc, #d5c93b);
  white-space: nowrap;
}

@keyframes cb-orbit-turn {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes cb-orbit-turn-back {
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
}
@keyframes cb-orbit-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(213,201,59,0.10), 0 6px 22px rgba(213,201,59,0.28); }
  50%      { box-shadow: 0 0 0 7px rgba(213,201,59,0.05), 0 6px 30px rgba(213,201,59,0.48); }
}

/* Le survol suspend la rotation pour laisser lire les drapeaux */
.cb-orbit:hover .cb-orbit-spin,
.cb-orbit:hover .cb-orbit-upright { animation-play-state: paused; }

@media (prefers-reduced-motion: reduce) {
  .cb-orbit-spin, .cb-orbit-upright, .cb-orbit-togo .cb-orbit-flag { animation: none; }
}
@media (max-width: 1200px) { .cb-orbit { --cb-orbit-scale: 0.82; } }
@media (max-width: 1000px) { .cb-orbit { --cb-orbit-scale: 0.66; } }
`;

export default function CedeaoOrbit({
  children,
  radius = 185,
  duration = 60,
  hostCode = 'TGO',
}) {
  const host = PAYS_CEDEAO.find((p) => p.code === hostCode);
  const orbiting = PAYS_CEDEAO.filter((p) => p.code !== hostCode);
  const HostFlag = FLAGS[host?.code2];

  const style = {
    '--cb-orbit-r': `${radius}px`,
    '--cb-orbit-duration': `${duration}s`,
  };

  return (
    <>
      <style>{css}</style>
      <div className="cb-orbit" style={style}>
        <div className="cb-orbit-rail" />
        <div className="cb-orbit-glow" />

        <div className="cb-orbit-spin">
          {orbiting.map((pays, i) => {
            const angle = (360 / orbiting.length) * i;
            const Flag = FLAGS[pays.code2];
            if (!Flag) return null;
            return (
              <div
                key={pays.code}
                className="cb-orbit-slot"
                style={{ transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${radius}px)` }}
              >
                {/* Annule l'angle de position, puis la rotation de l'anneau :
                    le drapeau reste droit tout au long du tour. */}
                <div style={{ transform: `rotate(${-angle}deg)` }}>
                  <div className="cb-orbit-upright">
                    <Flag className="cb-orbit-flag" title={pays.nom} aria-label={pays.nom} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {host && HostFlag && (
          <div className="cb-orbit-togo">
            <HostFlag className="cb-orbit-flag" title={host.nom} aria-label={host.nom} />
            <span className="cb-orbit-togo-label">{host.nom}</span>
          </div>
        )}

        <div className="cb-orbit-core">{children}</div>
      </div>
    </>
  );
}
